import { useMemo } from 'react';
import { numericFormatter } from 'react-number-format';
import { Button, CircularProgress, Typography } from '@mui/material';
import { parseExpression } from 'cron-parser';
import cronstrue from 'cronstrue';
import { sub } from 'date-fns';
import { makeStyles } from 'tss-react/mui';
import { AbiItem } from 'web3-utils';

import { useTranslation } from 'modules/i18n';

import { Signature } from '../Signature';
import translation from './confirmForm.translation';

interface Props {
  taskName: string;
  taskContractAddress: string;
  taskAbi?: AbiItem | null;
  params: string[];
  startImmediately: boolean;
  startDate: Date | null;
  cronDate?: string;
  onSubmit: () => void;
  isLoading?: boolean;
}

export function ConfirmForm({
  taskName,
  taskContractAddress,
  taskAbi,
  params,
  startDate,
  startImmediately,
  cronDate,
  onSubmit,
  isLoading,
}: Props): JSX.Element {
  const { classes } = useStyles();
  const { t, keys } = useTranslation(translation);

  const executionDate = useMemo(() => {
    if (!cronDate) {
      return null;
    }

    // needs for correct cron interval start
    const subbedStartDate = sub(startDate || new Date(), { seconds: 1 });

    const interval = parseExpression(cronDate, {
      currentDate: subbedStartDate,
    });

    const firstExecutionDate = interval.next().toDate();
    const isSameDay = firstExecutionDate.getDay() === new Date().getDay();
    if (isSameDay && startImmediately) {
      const cronItems = cronDate.split(' ');
      if (!cronItems[0]?.indexOf(',') && !cronItems[0]?.indexOf(',')) {
        return t(keys.now);
      }
    }

    return t(keys.at, { value: firstExecutionDate });
  }, [cronDate, keys.at, keys.now, startDate, startImmediately, t]);

  const intervalDescription = useMemo(() => {
    if (!cronDate) {
      return null;
    }

    return cronstrue.toString(cronDate);
  }, [cronDate]);

  return (
    <div data-tid="create-task-confirm" className={classes.container}>
      <Typography variant="button2">{t(keys.title)}</Typography>

      <div className={classes.list}>
        <div className={classes.listItem}>
          <Typography variant="subtitle3">{t(keys.taskName)}</Typography>

          <Typography
            data-tid="create-task-confirm-task-name"
            variant="body2"
            className={classes.textValue}
          >
            {taskName}
          </Typography>
        </div>

        <div className={classes.listItem}>
          <Typography variant="subtitle3">{t(keys.execute)}</Typography>

          <div className={classes.values}>
            <Typography
              data-tid="create-task-confirm-task-contract-address"
              variant="body2"
              className={classes.textValue}
            >
              {taskContractAddress}
            </Typography>

            {taskAbi && (
              <Signature
                dataTid="create-task-confirm-signature"
                variant="body2"
                value={taskAbi}
                className={classes.textValue}
              />
            )}

            <Typography
              variant="body2"
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              className={classes.textValue}
              data-tid="create-task-params"
            >
              {params.map((value, i) => {
                return (
                  // eslint-disable-next-line react/no-array-index-key
                  <span key={i} data-tid={`create-task-params-item-${i}`}>
                    {Number.isNaN(value)
                      ? value
                      : numericFormatter(value, {
                          valueIsNumericString: true,
                          thousandSeparator: ',',
                        })}
                  </span>
                );
              })}
            </Typography>
          </div>
        </div>

        {startDate && (
          <div className={classes.listItem}>
            <Typography variant="subtitle3">{t(keys.when)}</Typography>

            <div className={classes.values}>
              <Typography
                variant="body2"
                textAlign="right"
                className={classes.date}
                data-tid="create-task-confirm-start-date"
              >
                {t(keys.firstExecution, { value: executionDate }, true)}
              </Typography>

              <Typography
                variant="body2"
                textAlign="right"
                className={classes.date}
                data-tid="create-task-confirm-date"
              >
                {t(keys.then, { value: intervalDescription }, true)}
              </Typography>
            </div>
          </div>
        )}
      </div>

      <Button
        data-tid="create-task-confirm-submit-button"
        size="large"
        onClick={onSubmit}
        disabled={isLoading}
        sx={{ mt: 12 }}
      >
        {isLoading ? (
          <CircularProgress size={24} color="info" />
        ) : (
          t(keys.submit)
        )}
      </Button>
    </div>
  );
}

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: theme.spacing(8),
  },
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(5, 0),
    gap: theme.spacing(2),

    '&:not(:first-child)': {
      borderTop: `1px solid ${theme.palette.grey[100]}`,
    },
  },
  values: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  textValue: {
    wordBreak: 'break-all',
    textAlign: 'right',
  },
  date: {
    [theme.breakpoints.up('md')]: {
      '& br': {
        display: 'none',
      },
    },
  },
}));
